type PollCallback<T> = (value: T) => void;

type PollerConstructorArgs<T> = {
  interval?: number;
  onChange: PollCallback<T>;
  onError?: (e: unknown) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onMissPoll?: (value: any) => void;
};

export class Poller<T = any> {
  private interval: number;
  private asyncFn?: (arg?: any) => Promise<T>;
  private pollCallback: PollCallback<T> | null;
  private onError?: (e: unknown) => void;
  private pollRunning: boolean;
  private pollInterval: NodeJS.Timeout | null;
  private paused: boolean;
  private onMissPoll?: (value: any) => void;
  isLoading: boolean;
  private onLoadingChange?: (isLoading: boolean) => void;
  private hasMissedPoll?: boolean;

  constructor({
    interval = 0,
    onChange,
    onError,
    onLoadingChange,
    onMissPoll
  }: PollerConstructorArgs<T>) {
    this.interval = interval * 1000;
    this.pollCallback = onChange;
    this.onError = onError;
    this.pollRunning = false;
    this.pollInterval = null;
    this.isLoading = false;
    this.onLoadingChange = onLoadingChange;
    this.paused = false;
    this.onMissPoll = onMissPoll;
  }

  private async pollLoop() {
    if (this.paused) {
      this.onMissPoll?.(true);
      this.hasMissedPoll = true;
      return;
    }

    if (this.pollRunning && this.asyncFn && this.pollCallback) {
      try {
        this.onMissPoll?.(false);
        this.hasMissedPoll = false;
        this.setIsLoading();
        const value = await this.asyncFn();
        this.pollCallback(value);
      } catch (error) {
        this.onError?.(error);
      } finally {
        this.setLoadingStop();
      }
    }
  }

  startPolling(asyncFn: () => Promise<T>, interval?: number) {
    this.stopPolling();
    this.asyncFn = asyncFn;
    this.pollRunning = true;
    if (interval) {
      this.setInterval(interval);
    }
    this.pollLoop();

    this.pollInterval = setInterval(() => {
      this.pollLoop();
    }, this.interval);
  }

  private setInterval(interval: number) {
    this.interval = interval * 1000;
  }

  async pollOnce(asyncFn: () => Promise<T>) {
    if (this.paused) {
      this.onMissPoll?.(true);
      this.hasMissedPoll = true;
      return;
    }

    if (this.pollCallback) {
      this.stopPolling();
      this.setIsLoading();
      try {
        this.onMissPoll?.(false);
        this.hasMissedPoll = false;
        const value = await asyncFn();
        this.pollCallback(value);
      } catch (error) {
        this.onError?.(error);
      } finally {
        this.setLoadingStop();
      }
    }
  }

  doAsyncWithArg = async (p: any) => {
    if (this.asyncFn !== undefined) {
      const fn = () => this.asyncFn!(p);
      this.pollOnce(fn);
    }
  };

  setAsyncFn(fn: (p: any) => Promise<T>) {
    this.asyncFn = fn;
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.pollRunning = false;
  }

  setPaused(isPaused: boolean) {
    this.paused = isPaused;
  }

  needsUpdate() {
    return this.hasMissedPoll !== false && !this.isLoading;
  }

  private setIsLoading() {
    this.isLoading = true;
    this.onLoadingChange?.(true);
  }

  private setLoadingStop() {
    this.isLoading = false;
    this.onLoadingChange?.(false);
  }
}
